import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { getPasswordCriterions, isValidPassword } from '../../../services/account.service';
import axios, { AxiosError } from 'axios';
import { PasswordCriterionsDto } from '../../../services/dtos/password-criterions.dto';
import { validateNewPassword } from '../../../services/password-retrieve.service';
import ResetPasswordForm from '../../../components/forms/reset-password/reset-password.form';
import { NewPasswordDto } from '../../../services/dtos/new-password.dto';
import { httpCodeNotAcceptable } from '../../../utils/constants.utils';
import { showAxiosMessageError } from '../../../services/error-message.service';

/**
 * Page de la réinitialisation de mot de passe
 */
export default function ResetPasswordPage() {
    //Token envoyé dans l'url
    const { token } = useParams();
    const showMessage = useSnackbars();
    const navigate = useNavigate();
    //Message pour la validité du mot de passe
    const [messagesPassword, setMessagesPassword] = useState<string[]>([]);
    //Critères du mot de passe après un changement
    const [passwordCriterions, setPasswordCriterions] = useState<PasswordCriterionsDto | null>(null);
    //Disable ou non le bouton de la validation
    const [validate, setValidate] = useState(false);
    //Boolean permettant d'éviter le second lancement du userEffect
    const dataFetchedRef = useRef(false);

    //Vérification du mot de passe
    const handlerOnChangePassword = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        e.preventDefault();
        if (e?.target?.value.length > 0) {
            //Appel des civilités
            isValidPassword(e?.target?.value)
                .then(() => {
                    showMessage.infoAlert('Mot de passe validé');
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                });
        }
    };

    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;
        //Appel des critères pour les mots de passe
        getPasswordCriterions()
            .then(result => {
                //Ajout le message pour la majuscule
                if (result.upperCase) {
                    setMessagesPassword(oldArray => [...oldArray, 'une lettre en majuscule']);
                }
                //Ajout le message pour la minuscule
                if (result.lowerCase) {
                    setMessagesPassword(oldArray => [...oldArray, 'une lettre en minuscule']);
                }
                //Ajout le message pour le chiffre
                if (result.number) {
                    setMessagesPassword(oldArray => [...oldArray, 'un chiffre']);
                }
                //Ajout le message pour le caractère spécifique
                if (result.specialChars) {
                    setMessagesPassword(oldArray => [...oldArray, 'un caractère spécifique']);
                }
                //Ajout le message pour la taille
                if (result.maxLength !== undefined && result.minLength !== undefined) {
                    setMessagesPassword(oldArray => [
                        ...oldArray,
                        'au minimum ' + result.minLength + ' caractères et maximum ' + result.maxLength,
                    ]);
                }
                setPasswordCriterions(result);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            });
        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlerSubmit = (e: NewPasswordDto) => {
        // Empêche de pouvoir submit pendant le traitement
        setValidate(true);
        validateNewPassword(token, e.motDePasse)
            .then(result => {
                if (result.success) {
                    showMessage.successAlert(result.message);
                    navigate('/');
                } else {
                    navigate('/lien-expire');
                }
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
                if (axios.isAxiosError(error) && error?.response?.status === httpCodeNotAcceptable) {
                    navigate('/lien-expire');
                }
            })
            .finally(() => {
                setValidate(false);
            });
    };

    return (
        <ResetPasswordForm
            handlerSubmit={handlerSubmit}
            handlerOnChangePassword={handlerOnChangePassword}
            messagesPassword={messagesPassword}
            passwordCriterions={passwordCriterions}
            validate={validate}
        />
    );
}
